import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { TraceProductImages } from "../graphql/queries";
import { ProductImagesMap, EventProductInstance, ProductImageInfo } from "../types";


export function useProductImages() {
    const client = useApolloClient();
    const [productImages, setProductImages] = useState<ProductImagesMap>({});
    const [loading, setLoading] = useState<boolean>(false);
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const pendingEventIds = useRef<Set<string>>(new Set());

    const fetchProductImages = useCallback(async (eventProdInstances: EventProductInstance[]) => {
        const newInstances = eventProdInstances.filter(
            item => !fetchedEventIds.current.has(item.eventId) && !pendingEventIds.current.has(item.eventId)
        );

        if (newInstances.length === 0) return;

        newInstances.forEach(item => pendingEventIds.current.add(item.eventId));
        setLoading(true);

        const maxRetries = 2;
        let attempt = 0;
        let success = false;

        while (attempt < maxRetries && !success) {
            try {
                const { data } = await client.query({
                    query: TraceProductImages,
                    variables: {
                        input: {
                            eventProdInstancesInput: newInstances,
                            isAuth: false
                        }
                    },
                    fetchPolicy: "network-only",
                });

                const traceProductImages = data?.traceProductImages?.traceProductImages || [];

                const newProductImages: ProductImagesMap = {};
                traceProductImages.forEach((product: { eventId: string; instances: Array<{ instanceId: string; productImageInfoList: ProductImageInfo[] }> }) => {
                    // New structure: each product has an instances array
                    product.instances?.forEach((instance) => {
                        const firstImage = instance.productImageInfoList?.[0]?.presignedUrl || null;
                        // Store by productInstanceId for accurate per-product image mapping
                        if (instance.instanceId) {
                            newProductImages[instance.instanceId] = firstImage;
                        }
                    });
                    fetchedEventIds.current.add(product.eventId);
                });

                newInstances.forEach(item => {
                    fetchedEventIds.current.add(item.eventId);
                });

                setProductImages(prev => ({
                    ...prev,
                    ...newProductImages
                }));

                success = true;
            } catch (error) {
                attempt++;
                if (attempt >= maxRetries) {
                    newInstances.forEach(item => {
                        fetchedEventIds.current.add(item.eventId);
                    });
                }
            }
        }

        newInstances.forEach(item => pendingEventIds.current.delete(item.eventId));
        setLoading(false);
    }, [client]);

    const hasProductImage = useCallback((eventId: string): boolean => {
        return fetchedEventIds.current.has(eventId);
    }, []);

    return {
        productImages,
        loading,
        fetchProductImages,
        hasProductImage
    };
}
